import { useState } from 'react';
import type { Note, NoteVersion, Category } from '@voice/shared';
import type { AIReasoningOutput } from '@voice/shared';

interface AIReviewCardProps {
  note: Note;
  latestVersion: NoteVersion | null;
  categories: Category[];
  onApprove: (data: {
    intent: 'note' | 'task' | 'reminder';
    category_id: string | null;
    approve_category: boolean;
    accepted_action_indices: number[];
  }) => void;
  loading: boolean;
}

export function AIReviewCard({ note, latestVersion, categories, onApprove, loading }: AIReviewCardProps) {
  const aiOutput = latestVersion?.ai_output as AIReasoningOutput | null;

  const [intent, setIntent] = useState<'note' | 'task' | 'reminder'>(aiOutput?.intent ?? 'note');
  const [approveCategory, setApproveCategory] = useState(true);
  const [acceptedActions, setAcceptedActions] = useState<Set<number>>(
    new Set((aiOutput?.actions ?? []).map((_, i) => i))
  );

  if (!aiOutput?.meta) return null;

  const toggleAction = (idx: number) => {
    setAcceptedActions((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const category = note.category_id
    ? categories.find((c) => c.id === note.category_id)
    : null;

  const handleApprove = () => {
    onApprove({
      intent,
      category_id: note.category_id,
      approve_category: approveCategory,
      accepted_action_indices: Array.from(acceptedActions),
    });
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <span style={styles.headerTitle}>AI Review</span>
        <span style={styles.confidenceBadge}>
          {Math.round((note.confidence ?? 0) * 100)}% confident
        </span>
      </div>

      {/* Reasoning */}
      {aiOutput.meta.reasoning && (
        <p style={styles.reasoning}>{aiOutput.meta.reasoning}</p>
      )}

      {/* Intent selector */}
      <div style={styles.field}>
        <label style={styles.label}>Intent</label>
        <div style={styles.radioGroup}>
          {(['note', 'task', 'reminder'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              style={{
                ...styles.radioBtn,
                ...(intent === opt ? styles.radioBtnActive : {}),
              }}
              onClick={() => setIntent(opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Category */}
      {category && (
        <div style={styles.field}>
          <label style={styles.label}>Category</label>
          <div style={styles.categoryRow}>
            <span style={{
              ...styles.categoryName,
              color: category.color ?? 'var(--color-text-secondary)',
            }}>
              {category.name}
            </span>
            {!category.approved && (
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={approveCategory}
                  onChange={(e) => setApproveCategory(e.target.checked)}
                />
                <span style={styles.checkboxText}>Approve</span>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      {aiOutput.actions.length > 0 && (
        <div style={styles.field}>
          <label style={styles.label}>Suggested actions</label>
          {aiOutput.actions.map((action, idx) => (
            <label key={idx} style={styles.actionRow}>
              <input
                type="checkbox"
                checked={acceptedActions.has(idx)}
                onChange={() => toggleAction(idx)}
              />
              <span style={styles.actionType}>{action.type}</span>
              <span style={styles.actionTitle}>{action.title}</span>
              {action.due_date && (
                <span style={styles.actionDate}>{action.due_date}</span>
              )}
            </label>
          ))}
        </div>
      )}

      {/* Entities preview */}
      {(aiOutput.entities.people.length > 0 || aiOutput.entities.items.length > 0 || aiOutput.entities.locations.length > 0) && (
        <div style={styles.field}>
          <label style={styles.label}>Entities</label>
          <div style={styles.entityBadges}>
            {aiOutput.entities.people.map((p, i) => (
              <span key={`p-${i}`} style={styles.entityBadge}>{p.name}</span>
            ))}
            {aiOutput.entities.items.map((item, i) => (
              <span key={`i-${i}`} style={styles.entityBadge}>{item}</span>
            ))}
            {aiOutput.entities.locations.map((loc, i) => (
              <span key={`l-${i}`} style={styles.entityBadge}>{loc}</span>
            ))}
          </div>
        </div>
      )}

      {/* Apply button */}
      <button
        style={styles.applyBtn}
        onClick={handleApprove}
        disabled={loading}
        type="button"
      >
        {loading ? 'Applying...' : 'Apply'}
      </button>
    </div>
  );
}

const styles = {
  card: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-3)',
    padding: 'var(--space-3)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
  },
  confidenceBadge: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-warning)',
    padding: '1px 6px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
  },
  reasoning: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    lineHeight: 1.5,
    fontStyle: 'italic' as const,
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-1)',
  },
  label: {
    fontSize: '0.625rem',
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  radioGroup: {
    display: 'flex',
    gap: 'var(--space-1)',
  },
  radioBtn: {
    padding: '4px 12px',
    fontSize: 'var(--font-size-xs)',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
  },
  radioBtnActive: {
    backgroundColor: 'var(--color-accent-light)',
    borderColor: 'var(--color-accent)',
    color: 'var(--color-accent)',
    fontWeight: 600,
  },
  categoryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
  },
  categoryName: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
  },
  checkboxText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-1) 0',
    cursor: 'pointer',
    fontSize: 'var(--font-size-xs)',
  },
  actionType: {
    fontSize: '0.625rem',
    color: 'var(--color-accent)',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    flexShrink: 0,
  },
  actionTitle: {
    color: 'var(--color-text-secondary)',
    flex: 1,
  },
  actionDate: {
    color: 'var(--color-text-muted)',
    flexShrink: 0,
  },
  entityBadges: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '4px',
  },
  entityBadge: {
    fontSize: '0.625rem',
    color: 'var(--color-text-secondary)',
    padding: '1px 6px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
  },
  applyBtn: {
    padding: 'var(--space-2) var(--space-4)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: 'var(--color-accent)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    alignSelf: 'flex-end' as const,
  },
};
